import type {
  AssignmentDetail,
  CreateAssignment,
  CreateRole,
  Role,
  UpdateRole,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';

const tenantRolesBase = adminPath('/roles');
const projectRolesPath = (projectId: string) => adminPath(`/projects/${projectId}/roles`);
const assignmentsPath = (projectId: string) => adminPath(`/projects/${projectId}/assignments`);

/* ----------------------------- tenant role templates ----------------------------- */

/** `GET /admin/v1/roles`: tenant-wide role templates copied into new projects (array). */
export function useTenantRoles() {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.roles.list(),
    queryFn: ({ signal }) => request<Role[]>('GET', tenantRolesBase, { signal }),
  });
}

export function useCreateTenantRole() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateRole) => request<Role>('POST', tenantRolesBase, { body: input }),
    onSuccess: () => invalidate(keys.roles.all),
  });
}

export function useUpdateTenantRole() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateRole }) =>
      request<Role>('PATCH', `${tenantRolesBase}/${id}`, { body: patch }),
    onSuccess: () => invalidate(keys.roles.all),
  });
}

export function useDeleteTenantRole() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<unknown>('DELETE', `${tenantRolesBase}/${id}`),
    onSuccess: () => invalidate(keys.roles.all),
  });
}

/* ----------------------------- project roles ----------------------------- */

/** `GET /admin/v1/projects/{id}/roles` (array; system roles first). */
export function useProjectRoles(projectId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').roles,
    queryFn: ({ signal }) => request<Role[]>('GET', projectRolesPath(projectId ?? ''), { signal }),
    enabled: projectId !== undefined,
  });
}

export function useCreateProjectRole(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateRole) =>
      request<Role>('POST', projectRolesPath(projectId), { body: input }),
    onSuccess: () => invalidate(keys.project(projectId).roles, keys.projects.detail(projectId)),
  });
}

export function useUpdateProjectRole(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateRole }) =>
      request<Role>('PATCH', `${projectRolesPath(projectId)}/${id}`, { body: patch }),
    onSuccess: () =>
      invalidate(
        keys.project(projectId).roles,
        keys.project(projectId).assignments,
        keys.projects.detail(projectId),
      ),
  });
}

export function useDeleteProjectRole(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<unknown>('DELETE', `${projectRolesPath(projectId)}/${id}`),
    onSuccess: () =>
      invalidate(
        keys.project(projectId).roles,
        keys.project(projectId).assignments,
        keys.projects.detail(projectId),
      ),
  });
}

/* ----------------------------- assignments ----------------------------- */

/** `GET /admin/v1/projects/{id}/assignments`: every role assignment with grantee display data. */
export function useProjectAssignments(projectId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').assignments,
    queryFn: ({ signal }) =>
      request<AssignmentDetail[]>('GET', assignmentsPath(projectId ?? ''), { signal }),
    enabled: projectId !== undefined,
  });
}

/** `GET .../roles/{rid}/assignments` */
export function useRoleAssignments(projectId: string | undefined, roleId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').roleAssignments(roleId ?? ''),
    queryFn: ({ signal }) =>
      request<AssignmentDetail[]>(
        'GET',
        `${projectRolesPath(projectId ?? '')}/${roleId}/assignments`,
        {
          signal,
        },
      ),
    enabled: projectId !== undefined && roleId !== undefined,
  });
}

/** `POST .../roles/{rid}/assignments` with exactly one of `userId` / `groupId`. */
export function useCreateAssignment(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ roleId, ...input }: CreateAssignment & { roleId: string }) =>
      request<AssignmentDetail>('POST', `${projectRolesPath(projectId)}/${roleId}/assignments`, {
        body: input,
      }),
    onSuccess: (_a, { roleId }) =>
      invalidate(
        keys.project(projectId).assignments,
        keys.project(projectId).roleAssignments(roleId),
        keys.project(projectId).effectivePermissions(),
        keys.projects.all,
      ),
  });
}

/** `DELETE .../roles/{rid}/assignments/{aid}` */
export function useDeleteAssignment(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ roleId, assignmentId }: { roleId: string; assignmentId: string }) =>
      request<void>(
        'DELETE',
        `${projectRolesPath(projectId)}/${roleId}/assignments/${assignmentId}`,
      ),
    onSuccess: (_v, { roleId }) =>
      invalidate(
        keys.project(projectId).assignments,
        keys.project(projectId).roleAssignments(roleId),
        keys.project(projectId).effectivePermissions(),
        keys.projects.all,
      ),
  });
}
